import { FC, useEffect, useState } from "react";
import { MasterDataTable } from "../../../components/DataTable/MasterDataTable";
import { TableColumn } from "react-data-table-component";
import { Modal } from "bootstrap";
import { UpdateAttendance } from "../../../components/Attendance/UpdateAttendance";
import { MyAttendance } from "../../../interfaces/Attendance";
import moment from "moment";
import { AttendanceNote } from "../../../components/Attendance/AttendanceNote";
import { DateRangePicker } from "rsuite";
import { mutate } from "swr";
import { AttendanceAttachments } from "../../../components/Attendance/AttendanceAttachments";
import 'react-tippy/dist/tippy.css'
import { Tooltip } from 'react-tippy';
import 'moment-duration-format';
import { ApproveAttendance } from "../../../components/Attendance/ApproveAttendance";
import { calculateHaversineDistance, customNumberFormat, getColorByProgress } from "../../../functions/general";
import { MapDistanceAttendance } from "../../../components/Attendance/MapDistanceAttendance";
import startOfMonth from 'date-fns/startOfMonth';
import { DataTableExport } from "../../../components/DataTable/DataTableExport";
import useUser from "../../../hooks/User";
import { AttendanceInfo } from "../../../components/Attendance/AttendanceInfo";
import { useIntl } from "react-intl";
import usePredefinedRanges from "../../../hooks/PredefinedRanges";
import useCalendarLocale from "../../../hooks/CalendarLocale";
import useDynamicTab from "../../../hooks/useDynamicTab";
import { SuccessPack } from "../../../interfaces/SuccessPack";
import { useCompanyLimitStore } from "../../../stores/CompanyLimitStore";

const ReportSuccessPack: FC = () => {
    const API_URL = process.env.REACT_APP_API_URL
    const today = new Date()
    const { companyLimit } = useCompanyLimitStore()
    const [startDate, setStartDate] = useState<Date>(startOfMonth(today));
    const [endDate, setEndDate] = useState<Date>(today);
    const [loading, setLoading] = useState<boolean>(true);
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const user = useUser()
    const intl = useIntl();

    let isDataCompanyLimit = Object.keys(companyLimit).length > 0
    let limitSuccessPack = isDataCompanyLimit ? companyLimit.companyLimitSuccessPack : 0
    let usageSuccessPack = isDataCompanyLimit ? companyLimit.companyUsageSuccessPack : 0

    const predefinedRanges = usePredefinedRanges();
    const calendarLocale = useCalendarLocale();

    const convertMinuteToHours = (minutes: number) => {
        const hours = Math.floor(minutes / 60)
        return intl.formatMessage({ id: "FORM.LABEL.HOURS" }, { hours: hours })
    }

    const tableColumns: TableColumn<SuccessPack>[] = [
        {
            name: intl.formatMessage({ id: "MASTERS.DATATABLE.TYPE.DATE_USED" }),
            selector: row => row.dateUsage,
            sortable: true,
            cell: row => moment(row.dateUsage).utc().format('DD MMMM YYYY HH:mm')
        },
        {
            name: intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.NAME" }),
            selector: row => row.name,
            sortable: true,
        },
        {
            name: intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.DURATION" }),
            selector: row => row.type === "SUBTRACTION" ? row.duration * -1 : row.duration,
            sortable: true,
            cell: row => {
                let val = row.type === "SUBTRACTION" ? row.duration / 60 * -1 : row.duration / 60
                return customNumberFormat(val) + ' ' + intl.formatMessage({ id: "HOURS" })
            }
        },
        {
            name: intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.DESCRIPTION" }),
            selector: row => row.description,
            sortable: true,
        },
    ];

    useEffect(() => {
        if (startDate && endDate) {
            mutate(`${API_URL}all-user-attendances`);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 666) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        };

        // Initial check
        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useDynamicTab({ title: `${intl.formatMessage({ id: 'MENU.REPORT_WITH_TITLE' }, { title: 'Success Pack' })} | CRM WooBlazz` });

    return (
        <>
            <div className="row justify-content-between mb-3">
                <div className="col-lg-4 col-sm-3 col-xl-5" style={{ alignSelf: "center" }}>
                    <ol className="breadcrumb text-muted fs-6 fw-bold mb-5">
                        <li className="breadcrumb-item text-dark">Success Pack</li>
                    </ol>
                </div>
                <div className="col-lg-8 col-sm-9 col-xl-7">
                    <div className="d-flex float-end px-3 gap-5">
                        {
                            isDataCompanyLimit &&
                            <div className="d-flex align-items-center flex-column w-200px">
                                <div className="d-flex justify-content-between w-100 mt-auto mb-2">
                                    <span className="fw-bold fs-6 text-gray-400">
                                        {'Success Pack'}
                                    </span>
                                    <span className="fw-bolder fs-6">
                                        {convertMinuteToHours(parseInt(customNumberFormat(usageSuccessPack)))} / {convertMinuteToHours(parseInt(customNumberFormat(limitSuccessPack)))}
                                    </span>
                                </div>
                                <div className="h-5px mx-3 w-100 mb-3" style={{ backgroundColor: "white" }}>
                                    <div className={`rounded h-5px`} role="progressbar" style={{ backgroundColor: `${getColorByProgress(Math.round(usageSuccessPack / (limitSuccessPack) * 100))}`, width: (Math.round(usageSuccessPack / (limitSuccessPack) * 100) >= 100 ? 100 : Math.round(usageSuccessPack / (limitSuccessPack) * 100)) + "%" }}>
                                    </div>
                                </div>
                            </div>
                        }
                        <DateRangePicker
                            cleanable={false}
                            character=" - "
                            format="dd MMM yyyy"
                            locale={calendarLocale}
                            value={[startDate, endDate]}
                            ranges={predefinedRanges}
                            placement="bottomEnd"
                            onChange={(value) => {
                                if (value && value.length === 2) {
                                    setStartDate(value[0]);
                                    setEndDate(value[1]);
                                }
                            }}
                            showOneCalendar={isMobile}
                            disabled={loading}
                            isoWeek={true}
                        />
                    </div>
                </div>
            </div>
            <MasterDataTable
                tableKey="success-pack"
                tableColumns={tableColumns}
                apiURL={`success-pack-mutation?companyId=${user.data.company_id}&dateUsageFrom=${moment(startDate).format('YYYY-MM-DD')}&dateUsageTo=${moment(endDate).format('YYYY-MM-DD')}`}
                setLoading={setLoading}
                orderBy={1}
                order="asc"
            />
        </>
    )
}

export { ReportSuccessPack }